import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { InsightFilter, InsightOperator } from '@/shared/types/analytics';
import { Insight } from '@/shared/types/insights';

import { DEFAULT_OPERATORS, OPERATOR_LABELS } from './constants';

interface BooleanInsightFilterProps {
  filterIndex: number;
  insight: Insight;
}

export const BooleanInsightFilter = ({ filterIndex, insight }: BooleanInsightFilterProps) => {
  const { control, getValues } = useFormContext();
  const { t } = useTranslation();
  const { update } = useFieldArray({ control, name: 'filters' });

  // Get the insightFilters array
  const insightFilters = useWatch({ control, name: `filters.${filterIndex}.insightFilters` }) || [];

  // Check if this insight already exists in the filters
  const existingFilterIndex = insightFilters.findIndex(
    (filter: InsightFilter) => filter.id === insight.name
  );

  const currentFilter =
    existingFilterIndex !== -1
      ? insightFilters[existingFilterIndex]
      : {
          id: insight.name,
          category: insight.type,
          operator: DEFAULT_OPERATORS[insight.type] || 'eq',
          values: [],
        };

  const updateFilter = (newInsightFilter: InsightFilter) => {
    const updatedFilters = [...insightFilters];

    if (existingFilterIndex !== -1) {
      // Update existing filter
      updatedFilters[existingFilterIndex] = newInsightFilter;
    } else {
      // Add new filter
      updatedFilters.push(newInsightFilter);
    }

    const currentFormFilter = getValues(`filters.${filterIndex}`);
    update(filterIndex, { ...currentFormFilter, insightFilters: updatedFilters });
  };

  const handleOperatorChange = (newOperator: InsightOperator) => {
    // Reset values when operator changes - always empty for 'exists' operator
    updateFilter({ ...currentFilter, operator: newOperator, values: [] });
  };

  const handleChange = (value: string) => {
    const booleanValue = value === 'true';
    const newInsightFilter: InsightFilter = {
      id: insight.name,
      category: insight.type,
      operator: currentFilter.operator,
      values: [booleanValue],
    };

    updateFilter(newInsightFilter);
  };

  // Use the current value if it exists, otherwise no selection (empty string for RadioGroup)
  const currentValue = currentFilter.values?.[0];
  const radioValue = currentValue !== undefined ? currentValue.toString() : '';
  const operatorOptions = OPERATOR_LABELS[insight.type] || {};
  const isExistsOperator = currentFilter.operator === 'exists';

  return (
    <div className="space-y-3">
      <div role="presentation" onMouseDown={(e) => e.stopPropagation()}>
        <Select value={currentFilter.operator} onValueChange={handleOperatorChange}>
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(operatorOptions).map(([op, label]) => (
              <SelectItem key={op} value={op}>
                {t(label as string)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {!isExistsOperator ? (
        <RadioGroup value={radioValue} onValueChange={handleChange} className="gap-0">
          <div className="flex items-center space-x-2 rounded-sm px-2 py-1.5 hover:bg-highlight-background">
            <RadioGroupItem value="true" id={`${insight.insight_id}-true`} />
            <Label htmlFor={`${insight.insight_id}-true`} className="cursor-pointer font-normal">
              {t('common.true')}
            </Label>
          </div>
          <div className="flex items-center space-x-2 rounded-sm px-2 py-1.5 hover:bg-highlight-background">
            <RadioGroupItem value="false" id={`${insight.insight_id}-false`} />
            <Label htmlFor={`${insight.insight_id}-false`} className="cursor-pointer font-normal">
              {t('common.false')}
            </Label>
          </div>
        </RadioGroup>
      ) : (
        <div className="rounded-md border border-border bg-muted p-2 text-sm text-foreground-muted">
          {t('filters.exists_description', 'Any non-null value')}
        </div>
      )}
    </div>
  );
};
